import React, { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabaseClient'
import { Check, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL

const TestPage = () => {
  const { testId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  
  // CRITICAL: Track instance to detect StrictMode double-mount issues
  const instanceId = React.useRef(`TestPage-${Math.random().toString(36).substr(2, 9)}`)
  
  const [test, setTest] = useState(null)
  const [questions, setQuestions] = useState([])
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [answers, setAnswers] = useState({})
  const [loading, setLoading] = useState(true)
  const [isInitialized, setIsInitialized] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [error, setError] = useState('')
  const [showIntro, setShowIntro] = useState(true) // Show intro page first
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(null) // seconds
  const [timeExpired, setTimeExpired] = useState(false)
  
  // ULTRA MINIMAL MODE - bypasses entire UI tree for crash isolation
  const [ultraMinimalMode] = useState(true)

  // CRITICAL DEBUG: Track initialization state
  const [isFullyStable, setIsFullyStable] = useState(false)
  const renderCount = React.useRef(0)
  renderCount.current++

  console.log(`[${instanceId.current}] 🔄 RENDER #${renderCount.current}`, {
    loading,
    isInitialized,
    isFullyStable,
    hasQuestions: questions.length > 0,
    currentQuestionIndex,
    timestamp: Date.now()
  })

  useEffect(() => {
    console.log(`[${instanceId.current}] ✅ COMPONENT MOUNTED`)
    return () => {
      console.log(`[${instanceId.current}] ❌ COMPONENT UNMOUNTED`)
    }
  }, [])

  useEffect(() => {
    if (user && testId) {
      console.log(`[${instanceId.current}] 🚀 Starting loadTest()`)
      
      // CRITICAL: Track if component is still mounted
      let isMounted = true
      const loadAbortController = new AbortController()
      
      const loadTestSafe = async () => {
        try {
          const instance = instanceId.current
          console.log(`[${instance}] 📥 loadTest START`)
          setLoading(true)
          setIsInitialized(false)
          setIsFullyStable(false)
          
          // Get session
          let accessToken = null
          try {
            const { data: sessionData } = await supabase.auth.getSession()
            accessToken = sessionData?.session?.access_token
            console.log(`[${instance}] 🔐 Session obtained:`, !!accessToken)
          } catch (err) {
            console.error(`[${instance}] ❌ Session error:`, err)
          }

          if (!accessToken) {
            throw new Error('Autentifikácia zlyhala. Prosím prihláste sa znova.')
          }

          const headers = { 'Authorization': `Bearer ${accessToken}` }

          // Get test details
          console.log(`[${instance}] 📡 Fetching test details...`)
          const testResponse = await fetch(`${BACKEND_URL}/api/tests/${testId}`, { 
            headers,
            signal: loadAbortController.signal 
          })
          
          if (!isMounted) {
            console.log(`[${instance}] ⚠️ Component unmounted during fetch - aborting`)
            return
          }
          
          if (!testResponse.ok) {
            if (testResponse.status === 403) {
              throw new Error('Nemáte prístup k tomuto testu.')
            }
            if (testResponse.status === 404) {
              throw new Error('Test sa nenašiel.')
            }
            throw new Error('Nepodarilo sa načítať test')
          }
          const testData = await testResponse.json()
          console.log(`[${instance}] ✅ Test data received:`, testData.title)
          
          // Get questions
          console.log(`[${instance}] 📡 Fetching questions...`)
          const questionsResponse = await fetch(`${BACKEND_URL}/api/tests/${testId}/questions`, { 
            headers,
            signal: loadAbortController.signal
          })
          
          if (!isMounted) {
            console.log(`[${instance}] ⚠️ Component unmounted during fetch - aborting`)
            return
          }
          
          if (!questionsResponse.ok) {
            throw new Error('Nepodarilo sa načítať otázky')
          }
          const questionsData = await questionsResponse.json()
          console.log(`[${instance}] ✅ Questions received:`, questionsData.length, 'questions')
          
          // CRITICAL: Validate first question has options before setting state
          if (questionsData.length > 0) {
            const firstQ = questionsData[0]
            console.log(`[${instance}] 🔍 First question validation:`, {
              id: firstQ.id,
              type: firstQ.question_type,
              hasOptions: !!firstQ.options,
              optionsLength: firstQ.options?.length || 0
            })
          }
          
          // CRITICAL: Only set state if still mounted
          if (!isMounted) {
            console.log(`[${instance}] ⚠️ Component unmounted - skipping state update`)
            return
          }
          
          // Set all data at once to avoid partial renders
          setTest(testData)
          setQuestions(questionsData)
          
          // Initialize timer if test has duration
          if (testData.duration_minutes) {
            setTimeRemaining(testData.duration_minutes * 60) // Convert to seconds
            setTimeExpired(false)
          }
          
          console.log(`[${instance}] 💾 State updated - setting isInitialized=true`)
          setIsInitialized(true)
          
          // CRITICAL: Add small delay to ensure DOM is stable before allowing clicks
          await new Promise(resolve => setTimeout(resolve, 100))
          
          if (!isMounted) {
            console.log(`[${instance}] ⚠️ Component unmounted - skipping isFullyStable`)
            return
          }
          
          console.log(`[${instance}] 🎯 Setting isFullyStable=true`)
          setIsFullyStable(true)

        } catch (err) {
          if (err.name === 'AbortError') {
            console.log(`[${instanceId.current}] 🚫 loadTest ABORTED (component unmounted)`)
            return
          }
          console.error(`[${instanceId.current}] ❌ Load test error:`, err)
          if (isMounted) {
            setError(err.message || 'Nepodarilo sa načítať test')
          }
        } finally {
          if (isMounted) {
            setLoading(false)
            console.log(`[${instanceId.current}] 🏁 loadTest END`)
          }
        }
      }
      
      loadTestSafe()
      
      // CRITICAL: Cleanup - abort pending requests and prevent state updates
      return () => {
        console.log(`[${instanceId.current}] 🧹 Cleanup: Aborting pending loadTest`)
        isMounted = false
        loadAbortController.abort()
      }
    }
  }, [user, testId])

  // Timer countdown effect
  useEffect(() => {
    if (!isInitialized || timeRemaining === null || timeRemaining <= 0 || submitting) {
      return
    }

    const timerId = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          setTimeExpired(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timerId)
  }, [isInitialized, timeRemaining, submitting])

  const loadTest = async () => {
    const instance = instanceId.current
    try {
      console.log(`[${instance}] 📥 loadTest START`)
      setLoading(true)
      setIsInitialized(false)
      setIsFullyStable(false)
      
      // Get session
      let accessToken = null
      try {
        const { data: sessionData } = await supabase.auth.getSession()
        accessToken = sessionData?.session?.access_token
        console.log('[TestPage] 🔐 Session obtained:', !!accessToken)
      } catch (err) {
        console.error('[TestPage] ❌ Session error:', err)
      }

      if (!accessToken) {
        throw new Error('Autentifikácia zlyhala. Prosím prihláste sa znova.')
      }

      const headers = { 'Authorization': `Bearer ${accessToken}` }

      // Get test details
      console.log(`[${instance}] 📡 Fetching test details...`)
      const testResponse = await fetch(`${BACKEND_URL}/api/tests/${testId}`, { headers })
      if (!testResponse.ok) {
        throw new Error('Nepodarilo sa načítať test')
      }
      const testData = await testResponse.json()
      console.log(`[${instance}] ✅ Test data received:`, testData.title)
      
      // Get questions
      console.log(`[${instance}] 📡 Fetching questions...`)
      const questionsResponse = await fetch(`${BACKEND_URL}/api/tests/${testId}/questions`, { headers })
      if (!questionsResponse.ok) {
        throw new Error('Nepodarilo sa načítať otázky')
      }
      const questionsData = await questionsResponse.json()
      console.log(`[${instance}] ✅ Questions received:`, questionsData.length, 'questions')
      
      // CRITICAL: Validate first question has options before setting state
      if (questionsData.length > 0) {
        const firstQ = questionsData[0]
        console.log(`[${instance}] 🔍 First question validation:`, {
          id: firstQ.id,
          type: firstQ.question_type,
          hasOptions: !!firstQ.options,
          optionsLength: firstQ.options?.length || 0
        })
      }
      
      // Set all data at once to avoid partial renders
      setTest(testData)
      setQuestions(questionsData)
      
      // Initialize timer if test has duration
      if (testData.duration_minutes) {
        setTimeRemaining(testData.duration_minutes * 60) // Convert to seconds
        setTimeExpired(false)
      }
      
      console.log(`[${instance}] 💾 State updated - setting isInitialized=true`)
      setIsInitialized(true)
      
      // CRITICAL: Add small delay to ensure DOM is stable before allowing clicks
      await new Promise(resolve => setTimeout(resolve, 100))
      
      console.log(`[${instance}] 🎯 Setting isFullyStable=true`)
      setIsFullyStable(true)

    } catch (err) {
      console.error(`[${instance}] ❌ Load test error:`, err)
      setError(err.message || 'Nepodarilo sa načítať test')
    } finally {
      setLoading(false)
      console.log(`[${instance}] 🏁 loadTest END`)
    }
  }

  const handleAnswerChange = (questionId, answer) => {
    const instance = instanceId.current
    console.log(`[${instance}] 🖱️ ANSWER CLICK`, {
      questionId,
      answer,
      isFullyStable,
      currentQuestionId: currentQuestion?.id,
      hasOptions: !!currentQuestion?.options,
      timestamp: Date.now()
    })
    
    // CRITICAL: Guard against clicks during unstable state
    if (!isFullyStable) {
      console.warn(`[${instance}] ⚠️ CLICK IGNORED - not fully stable yet`)
      return
    }
    
    if (!currentQuestion || !currentQuestion.options) {
      console.warn(`[${instance}] ⚠️ CLICK IGNORED - invalid currentQuestion`)
      return
    }
    
    setAnswers(prev => {
      const newAnswers = { ...prev, [questionId]: answer }
      console.log(`[${instance}] ✅ Answer updated`)
      return newAnswers
    })
  }

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (seconds === null || seconds < 0) return ''
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const goToPrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1)
    }
  }

  const goToNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1)
    }
  }

  const jumpToQuestion = (index) => {
    setCurrentQuestionIndex(index)
  }

  const handleSubmitClick = () => {
    setShowConfirmModal(true)
  }

  const handleConfirmSubmit = async () => {
    try {
      setSubmitting(true)
      setShowConfirmModal(false)

      // Get session
      const { data: sessionData } = await supabase.auth.getSession()
      const accessToken = sessionData?.session?.access_token

      // Submit answers
      const response = await fetch(`${BACKEND_URL}/api/tests/${testId}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({ answers })
      })

      if (!response.ok) {
        throw new Error('Nepodarilo sa odoslať odpovede')
      }

      const result = await response.json()
      
      // Navigate to results page
      navigate(`/results/${result.attempt_id || testId}`)

    } catch (err) {
      console.error('Submit error:', err)
      setError(err.message || 'Nepodarilo sa odoslať test')
      setSubmitting(false)
    }
  }

  // Memoize current question for stability
  const currentQuestion = useMemo(() => {
    const instance = instanceId.current
    if (!isInitialized || questions.length === 0) {
      console.log(`[${instance}] currentQuestion = null (not ready)`)
      return null
    }
    const q = questions[currentQuestionIndex]
    console.log(`[${instance}] currentQuestion memoized:`, {
      id: q?.id,
      hasOptions: !!q?.options,
      optionsCount: q?.options?.length || 0
    })
    return q
  }, [isInitialized, questions, currentQuestionIndex])

  // Calculate progress
  const answeredCount = Object.keys(answers).length
  const totalQuestions = questions.length
  const progressPercent = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0

  // ============================================================================
  // CONTROLLED REINTRODUCTION MODE - Step 1: Header with progress bar
  // ============================================================================
  // IMPORTANT: Only activate ultraMinimalMode if NOT showing intro
  if (ultraMinimalMode && !showIntro && isInitialized && currentQuestion) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* STEP 1: Reintroduce header with progress - TESTING */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
          <div className="max-w-5xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between mb-3">
              <h1 className="text-xl font-bold text-gray-900">{test.title}</h1>
              <div className="flex items-center gap-4">
                {/* Timer display */}
                {timeRemaining !== null && (
                  <div className={`text-sm font-medium px-3 py-1.5 rounded-lg ${
                    timeExpired 
                      ? 'bg-red-100 text-red-700' 
                      : timeRemaining < 300 
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-blue-100 text-blue-700'
                  }`}>
                    ⏱️ {formatTime(timeRemaining)}
                  </div>
                )}
                <div className="text-sm text-gray-600">
                  Otázka {currentQuestionIndex + 1} z {totalQuestions}
                </div>
                <button
                  onClick={handleSubmitClick}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition"
                >
                  Ukončiť test
                </button>
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
            
            <div className="text-xs text-gray-500 mt-1">
              {answeredCount} z {totalQuestions} zodpovedaných
            </div>
          </div>
        </div>

        {/* Anti-translation notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 px-4 py-3 max-w-5xl mx-auto mt-4" translate="no">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="text-yellow-800 font-medium text-sm">Dôležité upozornenie</p>
              <p className="text-yellow-700 text-xs mt-1">
                Pred spustením testu odporúčame vypnúť automatický preklad stránky v prehliadači. Automatický preklad môže zmeniť význam otázok, odpovedí aj pokynov a ovplyvniť výsledok testu.
              </p>
            </div>
          </div>
        </div>

        {/* Time expired warning banner */}
        {timeExpired && (
          <div className="bg-red-50 border-l-4 border-red-500 px-4 py-3 max-w-5xl mx-auto mt-4">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <p className="text-red-800 font-medium">Čas vypršal</p>
                <p className="text-red-700 text-sm">Odporúčame vám test dokončiť a odoslať čo najskôr. Stále môžete pokračovať v odpovedaní.</p>
              </div>
            </div>
          </div>
        )}

        {/* Rest remains minimal */}
        <div style={{ 
          padding: '40px', 
          maxWidth: '800px',
          margin: '0 auto'
        }}>

        {/* V1: Passage rendering - simple and stable */}
        {currentQuestion.passage && (
          <div translate="no" style={{
            backgroundColor: '#f0f7ff',
            border: '2px solid #2563eb',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '30px'
          }}>
            <div style={{
              fontSize: '14px',
              color: '#1e40af',
              fontWeight: '600',
              marginBottom: '12px'
            }}>
              📖 {currentQuestion.passage.title}
            </div>
            <div style={{
              fontSize: '15px',
              lineHeight: '1.7',
              color: '#1f2937',
              whiteSpace: 'pre-wrap',
              fontFamily: 'Georgia, serif'
            }}>
              {currentQuestion.passage.content}
            </div>
          </div>
        )}

        {/* Bare minimum question */}
        <div style={{ marginBottom: '30px' }} translate="no">
          {/* Optional question image (renders above the question text for ALL question types) */}
          {currentQuestion.image_url && (
            <div style={{ marginBottom: '20px' }}>
              <img
                src={currentQuestion.image_url}
                alt="Obrázok k otázke"
                style={{
                  display: 'block',
                  maxWidth: '100%',
                  height: 'auto',
                  margin: '0 auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  backgroundColor: '#fafafa'
                }}
                onError={(e) => {
                  console.error('[TestPage] Question image failed to load:', currentQuestion.image_url)
                  e.target.style.display = 'none'
                }}
              />
            </div>
          )}

          <p style={{ 
            fontSize: '18px', 
            fontWeight: 'bold',
            marginBottom: '20px',
            lineHeight: '1.6',
            whiteSpace: 'pre-wrap'
          }}>
            {currentQuestion.question_text}
          </p>

          {/* ULTRA MINIMAL answer options - NO wrappers, NO helpers */}
          {currentQuestion.question_type === 'multiple_choice' && currentQuestion.options && (
            <div>
              {currentQuestion.options.map(opt => {
                const selected = answers[currentQuestion.id] === opt.key
                return (
                  <button
                    key={opt.key}
                    onClick={() => {
                      // Direct state update - no handleAnswerChange helper
                      setAnswers(prev => ({ ...prev, [currentQuestion.id]: opt.key }))
                    }}
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '15px',
                      marginBottom: '10px',
                      backgroundColor: selected ? '#d0e0ff' : '#ffffff',
                      border: selected ? '2px solid #0066cc' : '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '16px'
                    }}
                  >
                    <strong>{opt.key})</strong> {opt.text}
                  </button>
                )
              })}
            </div>
          )}

          {currentQuestion.question_type === 'short_answer' && (
            <textarea
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => {
                const val = e.target.value
                setAnswers(prev => ({ ...prev, [currentQuestion.id]: val }))
              }}
              rows="4"
              placeholder="Odpoveď..."
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
          )}

          {currentQuestion.question_type === 'numeric' && (
            <input
              type="number"
              value={answers[currentQuestion.id] || ''}
              onChange={(e) => {
                const val = e.target.value
                setAnswers(prev => ({ ...prev, [currentQuestion.id]: val }))
              }}
              placeholder="Číselná odpoveď..."
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '16px',
                border: '1px solid #ccc',
                borderRadius: '4px'
              }}
            />
          )}
        </div>

        {/* Bare minimum navigation */}
        <div style={{ 
          display: 'flex', 
          gap: '10px',
          borderTop: '2px solid #ccc',
          paddingTop: '20px'
        }}>
          <button
            onClick={() => {
              if (currentQuestionIndex > 0) {
                setCurrentQuestionIndex(currentQuestionIndex - 1)
              }
            }}
            disabled={currentQuestionIndex === 0}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              cursor: currentQuestionIndex === 0 ? 'not-allowed' : 'pointer',
              opacity: currentQuestionIndex === 0 ? 0.5 : 1,
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff'
            }}
          >
            ← Predchádzajúca
          </button>

          <button
            onClick={() => {
              if (currentQuestionIndex < questions.length - 1) {
                setCurrentQuestionIndex(currentQuestionIndex + 1)
              }
            }}
            disabled={currentQuestionIndex >= questions.length - 1}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              cursor: currentQuestionIndex >= questions.length - 1 ? 'not-allowed' : 'pointer',
              opacity: currentQuestionIndex >= questions.length - 1 ? 0.5 : 1,
              border: '1px solid #ccc',
              borderRadius: '4px',
              backgroundColor: '#fff'
            }}
          >
            Ďalšia →
          </button>

          {currentQuestionIndex === questions.length - 1 && (
            <button
              onClick={handleSubmitClick}
              style={{
                padding: '12px 24px',
                fontSize: '16px',
                marginLeft: 'auto',
                backgroundColor: '#28a745',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Odoslať test
            </button>
          )}
        </div>

        {/* Debug info */}
        </div>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Ukončiť test?</h3>
              <p className="text-gray-700 mb-2">
                Zodpovedali ste <strong>{answeredCount} z {totalQuestions}</strong> otázok.
              </p>
              {answeredCount < totalQuestions && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
                  <p className="text-orange-800 text-sm font-medium">
                    ⚠️ Upozornenie
                  </p>
                  <p className="text-orange-700 text-sm mt-1">
                    Nezodpovedané otázky ({totalQuestions - answeredCount}) budú hodnotené ako nesprávne a získate za ne 0 bodov.
                  </p>
                </div>
              )}
              <p className="text-gray-600 mb-6">
                Po ukončení testu už nebudete môcť zmeniť odpovede. Chcete test odoslať?
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                >
                  Pokračovať v teste
                </button>
                <button
                  onClick={handleConfirmSubmit}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Odoslať test
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submitting overlay */}
        {submitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Odosielam test...</p>
            </div>
          </div>
        )}
      </div>
    )
  }
  // ============================================================================
  // END ULTRA MINIMAL MODE
  // ============================================================================

  // Show error state FIRST (error may occur before initialization, e.g. 403)
  if (error) {
    console.log('[TestPage] ❌ RENDERING PATH: ERROR STATE', {
      error,
      timestamp: Date.now()
    })
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center" data-testid="test-page-error">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Chyba</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="test-page-error-back-btn"
          >
            Späť na dashboard
          </button>
        </div>
      </div>
    )
  }

  // Show loading ONLY before initialization
  if (loading || !isInitialized) {
    console.log('[TestPage] 🔄 RENDERING PATH: LOADING STATE', {
      loading,
      isInitialized,
      timestamp: Date.now()
    })
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Načítavam test...</p>
        </div>
      </div>
    )
  }

  // Show intro page before test starts (HIGHEST PRIORITY - before empty state check)
  console.log('[TestPage-Intro] Checking intro page condition:', {
    showIntro,
    hasTest: !!test,
    hasIntroText: !!test?.intro_text,
    introTextValue: test?.intro_text,
    timestamp: Date.now()
  })
  
  if (showIntro && test) {
    console.log('[TestPage-Intro] ✅ RENDERING INTRO PAGE')
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-3xl mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Test Title */}
            <div className="text-center mb-8">
              <div className="text-5xl mb-4">📝</div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                {test.title}
              </h1>
              {test.description && (
                <p className="text-gray-600 mt-2">{test.description}</p>
              )}
            </div>

            {/* Intro Text */}
            {test.intro_text && (
              <div className="mb-6">
                <div className="bg-blue-50 border-l-4 border-blue-400 p-6 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Úvod</h3>
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {test.intro_text}
                  </p>
                </div>
              </div>
            )}

            {/* Topics */}
            {test.topics_text && (
              <div className="mb-6">
                <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
                  <h3 className="font-bold text-gray-900 mb-2">Témy</h3>
                  <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                    {test.topics_text}
                  </p>
                </div>
              </div>
            )}

            {/* Test Info */}
            <div className="bg-gray-50 rounded-xl p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">⏱️</div>
                  <div>
                    <div className="text-sm text-gray-600">Časový limit</div>
                    <div className="font-bold text-gray-900">{test.duration_minutes} minút</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-2xl">📋</div>
                  <div>
                    <div className="text-sm text-gray-600">Počet otázok</div>
                    <div className="font-bold text-gray-900">{questions?.length || 0} otázok</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-lg mb-8">
              <p className="text-sm text-gray-700">
                <strong>💡 Tip:</strong> Riešenia a vysvetlenia sa zobrazia po odoslaní testu.
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex-1 px-6 py-4 border-2 border-gray-300 rounded-xl font-bold text-gray-700 hover:bg-gray-50 transition"
              >
                ← Späť
              </button>
              <button
                onClick={() => setShowIntro(false)}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-600 transition shadow-lg"
              >
                Začať test →
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show empty state
  if (!test || questions.length === 0) {
    console.log('[TestPage] 📭 RENDERING PATH: EMPTY STATE', {
      hasTest: !!test,
      questionsLength: questions.length,
      timestamp: Date.now()
    })
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <p className="text-gray-600 mb-6">Test nemá žiadne otázky</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Späť na dashboard
          </button>
        </div>
      </div>
    )
  }

  // Final safety check
  if (!currentQuestion || 
      !currentQuestion.id || 
      !currentQuestion.question_type ||
      !currentQuestion.question_text) {
    console.log('[TestPage] ⚠️ RENDERING PATH: INVALID CURRENT QUESTION', {
      hasCurrentQuestion: !!currentQuestion,
      currentQuestionId: currentQuestion?.id,
      currentQuestionType: currentQuestion?.question_type,
      timestamp: Date.now()
    })
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <p className="text-gray-600 mb-6">Chyba pri načítaní otázky</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Späť na dashboard
          </button>
        </div>
      </div>
    )
  }

  console.log('[TestPage] ✅ RENDERING PATH: MAIN UI', {
    loading,
    isInitialized,
    isFullyStable,
    currentQuestionId: currentQuestion.id,
    currentQuestionType: currentQuestion.question_type,
    hasOptions: !!currentQuestion.options,
    optionsLength: currentQuestion.options?.length || 0,
    currentQuestionIndex,
    totalQuestions: questions.length,
    answersCount: Object.keys(answers).length,
    timestamp: Date.now()
  })

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with progress */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <h1 className="text-xl font-bold text-gray-900">{test.title}</h1>
            <div className="text-sm text-gray-600">
              Otázka {currentQuestionIndex + 1} z {totalQuestions}
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          
          <div className="text-xs text-gray-500 mt-1">
            {answeredCount} z {totalQuestions} zodpovedaných
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question list sidebar - hidden on mobile */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm p-4 sticky top-24">
              <h3 className="font-semibold text-gray-900 mb-3">Otázky</h3>
              <div className="grid grid-cols-5 gap-2">
                {questions.map((q, index) => {
                  const isAnswered = answers[q.id] !== undefined
                  const isCurrent = index === currentQuestionIndex
                  
                  return (
                    <button
                      key={q.id}
                      onClick={() => jumpToQuestion(index)}
                      className={`
                        w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium
                        transition-all
                        ${isCurrent ? 'ring-2 ring-blue-600 bg-blue-50 text-blue-600' : ''}
                        ${!isCurrent && isAnswered ? 'bg-green-100 text-green-700' : ''}
                        ${!isCurrent && !isAnswered ? 'bg-gray-100 text-gray-600 hover:bg-gray-200' : ''}
                      `}
                    >
                      {isAnswered && !isCurrent && (
                        <Check className="w-4 h-4" />
                      )}
                      {!isAnswered && (index + 1)}
                      {isCurrent && (index + 1)}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Main question area */}
          <div className="lg:col-span-3">
            {/* REMOVED key={currentQuestion.id} to prevent unmount during interaction */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
              {/* Optional question image */}
              {currentQuestion.image_url && (
                <div className="mb-6">
                  <div className="relative rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                    <img 
                      src={currentQuestion.image_url} 
                      alt="Obrázok k otázke" 
                      className="w-full h-auto max-w-2xl mx-auto"
                      onError={(e) => {
                        console.error('Image load error:', e)
                        e.target.style.display = 'none'
                      }}
                    />
                  </div>
                </div>
              )}
              
              {/* Question text */}
              <div className="mb-6">
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-semibold text-sm">
                    {currentQuestionIndex + 1}
                  </span>
                  <div className="flex-1">
                    <h2 className="text-lg font-medium text-gray-900 leading-relaxed whitespace-pre-wrap">
                      {currentQuestion.question_text}
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">
                      {currentQuestion.points} {currentQuestion.points === 1 ? 'bod' : 'body'}
                    </p>
                  </div>
                </div>
              </div>

              {/* ========== HARD ISOLATION TEST - PLAIN HTML ONLY ========== */}
              {/* NO custom components, NO animations, NO wrappers */}
              {/* NO console.log in render path - testing if crash is in answer subtree */}
              <div style={{ marginTop: '24px' }}>
                <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                  🧪 Hard Isolation Mode: Plain HTML only (no React components)
                </p>
                
                {currentQuestion.question_type === 'multiple_choice' && (
                  <div>
                    {currentQuestion.options && currentQuestion.options.map((opt) => {
                      const isSelected = answers[currentQuestion.id] === opt.key
                      return (
                        <button
                          key={opt.key}
                          onClick={() => handleAnswerChange(currentQuestion.id, opt.key)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '16px',
                            marginBottom: '8px',
                            border: isSelected ? '2px solid #3b82f6' : '2px solid #d1d5db',
                            borderRadius: '8px',
                            backgroundColor: isSelected ? '#eff6ff' : '#ffffff',
                            cursor: 'pointer',
                            fontSize: '16px'
                          }}
                        >
                          <strong style={{ color: '#3b82f6' }}>{opt.key})</strong> {opt.text}
                        </button>
                      )
                    })}
                  </div>
                )}

                {currentQuestion.question_type === 'short_answer' && (
                  <textarea
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    rows="4"
                    placeholder="Napíšte vašu odpoveď..."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                )}

                {currentQuestion.question_type === 'numeric' && (
                  <input
                    type="number"
                    value={answers[currentQuestion.id] || ''}
                    onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                    placeholder="Napíšte číselná odpoveď..."
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '16px'
                    }}
                  />
                )}
              </div>
              {/* ========== END HARD ISOLATION TEST ========== */}
            </div>

            {/* Navigation buttons */}
            <div className="flex items-center justify-between gap-4">
              <button
                onClick={goToPrevious}
                disabled={currentQuestionIndex === 0}
                className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="w-5 h-5" />
                Predchádzajúca
              </button>

              {currentQuestionIndex < questions.length - 1 ? (
                <button
                  onClick={goToNext}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all"
                >
                  Ďalšia
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  onClick={handleSubmitClick}
                  disabled={submitting}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {submitting ? 'Odosielam...' : 'Odoslať test'}
                </button>
              )}
            </div>

            {/* Mobile question navigator */}
            <div className="lg:hidden mt-6 bg-white rounded-lg shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Rýchly prechod</h3>
              <div className="grid grid-cols-8 gap-2">
                {questions.map((q, index) => {
                  const isAnswered = answers[q.id] !== undefined
                  const isCurrent = index === currentQuestionIndex
                  
                  return (
                    <button
                      key={q.id}
                      onClick={() => jumpToQuestion(index)}
                      className={`
                        h-10 rounded-lg flex items-center justify-center text-xs font-medium
                        ${isCurrent ? 'ring-2 ring-blue-600 bg-blue-50 text-blue-600' : ''}
                        ${!isCurrent && isAnswered ? 'bg-green-100 text-green-700' : ''}
                        ${!isCurrent && !isAnswered ? 'bg-gray-100 text-gray-600' : ''}
                      `}
                    >
                      {isAnswered && !isCurrent ? <Check className="w-3 h-3" /> : (index + 1)}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-2">Odoslať test?</h3>
            <p className="text-gray-600 mb-1">
              Zodpovedali ste {answeredCount} z {totalQuestions} otázok.
            </p>
            {answeredCount < totalQuestions && (
              <p className="text-orange-600 text-sm mb-4">
                ⚠️ Niektoré otázky zostali nezodpovedané.
              </p>
            )}
            <p className="text-gray-600 mb-6">
              Po odoslaní už nebudete môcť zmeniť odpovede. Chcete pokračovať?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Zrušiť
              </button>
              <button
                onClick={handleConfirmSubmit}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
              >
                Odoslať
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submitting overlay */}
      {submitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Odosielam test...</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default TestPage
